
interface FileSystemEntry {
    isFile: boolean;
    isDirectory: boolean;
    name: string;
    fullPath: string;
    filesystem: any;
}

interface FileSystemFileEntry extends FileSystemEntry {
    file(successCallback: (file: File) => void, errorCallback?: (error: Error) => void): void;
}

interface FileSystemDirectoryEntry extends FileSystemEntry {
    createReader(): FileSystemDirectoryReader;
}

interface FileSystemDirectoryReader {
    readEntries(successCallback: (entries: FileSystemEntry[]) => void, errorCallback?: (error: Error) => void): void;
}

const readDirectory = (reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> => {
    return new Promise((resolve, reject) => {
        const entries: FileSystemEntry[] = [];
        
        const read = () => {
            reader.readEntries((results) => {
                if (results.length === 0) {
                    resolve(entries);
                } else {
                    entries.push(...results);
                    read();
                }
            }, reject);
        };
        
        read();
    });
};

const processEntry = async (entry: FileSystemEntry, parentPath: string = ''): Promise<File[]> => {
    if (entry.isFile) {
        return new Promise((resolve, reject) => {
            (entry as FileSystemFileEntry).file((file) => {

                const path = parentPath ? `${parentPath}/${entry.name}` : entry.name;
                
                try {
                    Object.defineProperty(file, 'webkitRelativePath', {
                        value: path,
                        writable: true,
                        configurable: true
                    });
                } catch (e) {
                    console.warn('Could not set webkitRelativePath on file', e);
                }
                (file as any).itemPath = path;
                
                resolve([file]);
            }, reject);
        });
    } if (entry.isDirectory) {
        const dirReader = (entry as FileSystemDirectoryEntry).createReader();
        const entries = await readDirectory(dirReader);
        const files: File[] = [];

        const currentPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;

        for (const childEntry of entries) {
            const childFiles = await processEntry(childEntry, currentPath);
            files.push(...childFiles);
        }
        return files;
    }
    return [];
};

export const scanFiles = async (items: DataTransferItemList): Promise<{ files: File[], topLevelFolders: string[] }> => {
    const files: File[] = [];
    const entries: FileSystemEntry[] = [];
    const topLevelFolders: string[] = [];

    for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (item.kind === 'file') {
            const itemAny = item as any;
            const getEntry = itemAny.getAsEntry || itemAny.webkitGetAsEntry;
            
            if (getEntry) {
                const entry = getEntry.call(item) as FileSystemEntry;
                if (entry) {
                    entries.push(entry);
                    if (entry.isDirectory) {
                        topLevelFolders.push(entry.name);
                    }
                }
            } else {
                const file = item.getAsFile();
                if (file) files.push(file);
            }
        }
    }

    for (const entry of entries) {
        // Start processing with empty parent path for top-level items
        const entryFiles = await processEntry(entry, '');
        files.push(...entryFiles);
    }

    return { files, topLevelFolders };
};
